import type {
  StockClipForReview,
  StockClipTagStatus,
} from '@borradh-workspace/api-client/types';
import { bodyRegionLabels } from '@borradh-workspace/api-client/types';
import {
  AlertTriangleIcon,
  ChevronLeft,
  ChevronRight,
  FilmIcon,
  PartyPopper,
  RefreshCwIcon,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';

import {
  STOCK_REVIEW_PAGE_SIZE,
  useReviewStockClip,
  useStockClipsForReview,
} from '../api';
import { OrgCombobox, type SelectedOrg } from './org-combobox';
import { OrganizationStockFootage } from './organization-stock-footage';
import { StockClipPlayer } from './stock-footage/stock-clip-player';
import {
  type StockClipDecision,
  StockClipReviewPanel,
} from './stock-footage/stock-clip-review-panel';
import {
  type StockClipTags,
  type Technique,
  clipTitle,
} from './stock-footage/stock-clip-tags-editor';

/** How many waiting clips the one-at-a-time queue loads at once. */
const QUEUE_SIZE = 50;

type Tab = StockClipTagStatus | 'clinics';

/**
 * Where Borradh staff look after the stock footage bank. The AI tags each
 * clip (from the picture, compared with the stock title) and it is live
 * straight away; staff check, fix or reject clips here, one at a time. The
 * Clinics tab shows one clinic's services and the clips each one uses
 * (docs/plans/stock-footage-tagging.md).
 */
export function StockFootageReview() {
  const [tab, setTab] = useState<Tab>('pending');
  // A one-row read for the counts on the tabs and the progress bar.
  const {
    page: summary,
    isFetching,
    refetch,
  } = useStockClipsForReview({
    status: 'pending',
    offset: 0,
    limit: 1,
  });
  const counts = summary?.counts;
  const total = counts
    ? counts.pending + counts.confirmed + counts.rejected
    : 0;
  const reviewed = counts ? counts.confirmed + counts.rejected : 0;

  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="flex flex-wrap items-center justify-between gap-4">
          <div className="min-w-56 flex-1 space-y-2">
            <div className="flex items-baseline justify-between gap-2 text-sm">
              <span className="font-medium">
                {counts
                  ? counts.pending === 0
                    ? 'Every clip has been checked'
                    : `${counts.pending} ${counts.pending === 1 ? 'clip' : 'clips'} set by the AI. Review is optional; corrections and rejections override AI.`
                  : 'Loading…'}
              </span>
              <span className="text-muted-foreground">
                {reviewed} of {total} checked
              </span>
            </div>
            <Progress
              value={total > 0 ? (reviewed / total) * 100 : 0}
              aria-label="Review progress"
            />
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => void refetch()}
            disabled={isFetching}
          >
            <RefreshCwIcon className={isFetching ? 'animate-spin' : ''} />
            Refresh
          </Button>
        </CardContent>
      </Card>

      <Tabs value={tab} onValueChange={(value) => setTab(value as Tab)}>
        <TabsList>
          <TabsTrigger value="pending">
            Set by AI{counts ? ` (${counts.pending})` : ''}
          </TabsTrigger>
          <TabsTrigger value="confirmed">
            Checked{counts ? ` (${counts.confirmed})` : ''}
          </TabsTrigger>
          <TabsTrigger value="rejected">
            Rejected{counts ? ` (${counts.rejected})` : ''}
          </TabsTrigger>
          <TabsTrigger value="clinics">Clinics</TabsTrigger>
        </TabsList>
      </Tabs>

      {tab === 'clinics' ? (
        <ClinicStockFootage />
      ) : tab === 'pending' ? (
        <ReviewQueue />
      ) : (
        <DecidedClips key={tab} status={tab} />
      )}
    </div>
  );
}

/**
 * The waiting clips, one at a time. A decision moves straight on to the next
 * clip; Previous and Skip (or the arrow keys) move without deciding. Clips
 * where the picture and the title disagree come first.
 */
function ReviewQueue() {
  const [offset, setOffset] = useState(0);
  const [index, setIndex] = useState(0);
  const [decided, setDecided] = useState<ReadonlySet<string>>(new Set());
  const { page, isLoading, isError, error } = useStockClipsForReview({
    status: 'pending',
    offset,
    limit: QUEUE_SIZE,
  });
  const { review, reviewingId } = useReviewStockClip();
  // Take focus when the queue opens, so the keyboard shortcuts work at once
  // instead of the arrow keys still driving the tab bar that was just clicked.
  const queueRef = useRef<HTMLDivElement>(null);
  const ready = Boolean(page);
  useEffect(() => {
    if (!ready) return;
    // Next frame: a click on the tab focuses the tab after this effect runs.
    const frame = requestAnimationFrame(() =>
      queueRef.current?.focus({ preventScroll: true })
    );
    return () => cancelAnimationFrame(frame);
  }, [ready]);

  const items = page?.items ?? [];
  const visible = items.filter((c) => !decided.has(c.id));
  const clip = visible[index] ?? null;
  const decidedButListed = items.filter((c) => decided.has(c.id)).length;
  const waiting = Math.max(0, (page?.counts.pending ?? 0) - decidedButListed);
  const hasLaterPage = offset + items.length < (page?.counts.pending ?? 0);

  // Deciding the last clip in view leaves the index past the end; step back.
  useEffect(() => {
    if (visible.length > 0 && index > visible.length - 1) {
      setIndex(visible.length - 1);
    }
  }, [visible.length, index]);

  const next = () => {
    if (index + 1 < visible.length) setIndex(index + 1);
    else if (hasLaterPage) {
      setOffset(offset + QUEUE_SIZE);
      setIndex(0);
    }
  };
  const previous = () => {
    if (index > 0) setIndex(index - 1);
    else if (offset > 0) {
      setOffset(Math.max(0, offset - QUEUE_SIZE));
      setIndex(QUEUE_SIZE - 1);
    }
  };

  const decide = (
    target: StockClipForReview,
    decision: StockClipDecision,
    tags: StockClipTags
  ) =>
    review(target.id, { decision, ...tags }, () =>
      setDecided((prev) => new Set(prev).add(target.id))
    );

  if (isLoading) return <PanelSkeleton />;
  if (isError) return <LoadError message={error?.message} />;

  if (!clip) {
    return waiting === 0 ? (
      <Empty>
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <PartyPopper />
          </EmptyMedia>
          <EmptyTitle>All caught up</EmptyTitle>
          <EmptyDescription>
            Every clip in the bank has been reviewed. New clips will show up
            here.
          </EmptyDescription>
        </EmptyHeader>
      </Empty>
    ) : (
      <Empty>
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <FilmIcon />
          </EmptyMedia>
          <EmptyTitle>End of the queue</EmptyTitle>
          <EmptyDescription>
            You skipped {waiting} {waiting === 1 ? 'clip' : 'clips'}. They are
            still waiting for a decision.
          </EmptyDescription>
        </EmptyHeader>
        <EmptyContent>
          <Button
            onClick={() => {
              setOffset(0);
              setIndex(0);
            }}
          >
            Go back to the first one
          </Button>
        </EmptyContent>
      </Empty>
    );
  }

  const position = offset + index + 1;
  return (
    <Card ref={queueRef} tabIndex={-1} className="outline-none">
      <CardContent>
        <StockClipReviewPanel
          key={clip.id}
          clip={clip}
          catalogue={page?.catalogue ?? []}
          techniques={page?.techniques ?? []}
          isSaving={reviewingId === clip.id}
          autoPlay
          onDecide={(decision, tags) => decide(clip, decision, tags)}
          shortcuts={{ onPrevious: previous, onNext: next }}
          header={
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-muted-foreground text-sm">
                Clip {position} of {waiting}
              </span>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={previous}
                  disabled={index === 0 && offset === 0}
                >
                  <ChevronLeft />
                  Previous
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={next}
                  disabled={index + 1 >= visible.length && !hasLaterPage}
                >
                  Skip
                  <ChevronRight />
                </Button>
              </div>
            </div>
          }
        />
        <p className="mt-6 text-center text-muted-foreground text-xs">
          Shortcuts: C to confirm, R to reject, left and right arrows to move
          between clips.
        </p>
      </CardContent>
    </Card>
  );
}

/** Confirmed or rejected clips, as a grid; any decision can be changed. */
function DecidedClips({
  status,
}: { status: Exclude<StockClipTagStatus, 'pending'> }) {
  const [offset, setOffset] = useState(0);
  const [editing, setEditing] = useState<StockClipForReview | null>(null);
  const { page, isLoading, isError, error, isFetching } =
    useStockClipsForReview({ status, offset });
  const { review, reviewingId } = useReviewStockClip();
  const techniques: Technique[] = page?.techniques ?? [];

  const items = page?.items ?? [];
  const total = page?.counts[status] ?? 0;

  // Changing the last decisions on a later page empties it; step back.
  useEffect(() => {
    if (page && page.items.length === 0 && offset > 0) {
      setOffset((o) => Math.max(0, o - STOCK_REVIEW_PAGE_SIZE));
    }
  }, [page, offset]);

  if (isLoading) return <PanelSkeleton />;
  if (isError) return <LoadError message={error?.message} />;
  if (items.length === 0) {
    return (
      <Empty>
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <FilmIcon />
          </EmptyMedia>
          <EmptyTitle>
            {status === 'confirmed'
              ? 'Nothing confirmed yet'
              : 'Nothing rejected'}
          </EmptyTitle>
          <EmptyDescription>
            {status === 'confirmed'
              ? 'Clips you confirm show up here, and can be changed later.'
              : 'Clips you reject show up here, and can be brought back.'}
          </EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {items.map((clip) => (
          <Card key={clip.id} className="gap-3 py-3">
            <CardContent className="space-y-3 px-3">
              <StockClipPlayer clip={clip} />
              <div className="space-y-2">
                <p className="font-medium text-sm">
                  {clipTitle(clip, techniques)}
                </p>
                {clip.regions.length > 0 ? (
                  <div className="flex flex-wrap gap-1">
                    {clip.regions.map((r) => (
                      <Badge key={r} variant="secondary">
                        {bodyRegionLabels[r]}
                      </Badge>
                    ))}
                  </div>
                ) : null}
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate text-muted-foreground text-xs">
                    {clip.reviewedByName && clip.reviewedAt
                      ? `${clip.reviewedByName}, ${new Date(clip.reviewedAt).toLocaleDateString()}`
                      : 'Reviewed before this page existed'}
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setEditing(clip)}
                  >
                    Change
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {total > STOCK_REVIEW_PAGE_SIZE ? (
        <div className="flex items-center justify-between gap-3">
          <p className="text-muted-foreground text-sm">
            {offset + 1} to {Math.min(offset + STOCK_REVIEW_PAGE_SIZE, total)}{' '}
            of {total}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={offset === 0 || isFetching}
              onClick={() =>
                setOffset((o) => Math.max(0, o - STOCK_REVIEW_PAGE_SIZE))
              }
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={offset + STOCK_REVIEW_PAGE_SIZE >= total || isFetching}
              onClick={() => setOffset((o) => o + STOCK_REVIEW_PAGE_SIZE)}
            >
              Next
            </Button>
          </div>
        </div>
      ) : null}

      <Dialog
        open={editing !== null}
        onOpenChange={(open) => {
          if (!open) setEditing(null);
        }}
      >
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-5xl">
          <DialogHeader>
            <DialogTitle>Change this clip</DialogTitle>
            <DialogDescription>
              {status === 'confirmed'
                ? 'Fix its tags and confirm again, or reject it.'
                : 'Confirm it to bring it back, or keep it rejected.'}
            </DialogDescription>
          </DialogHeader>
          {editing ? (
            <StockClipReviewPanel
              key={editing.id}
              clip={editing}
              catalogue={page?.catalogue ?? []}
              techniques={techniques}
              isSaving={reviewingId === editing.id}
              onDecide={(decision, tags) =>
                review(editing.id, { decision, ...tags }, () =>
                  setEditing(null)
                )
              }
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PanelSkeleton() {
  return (
    <Card>
      <CardContent className="grid gap-6 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)]">
        <Skeleton className="aspect-video w-full" />
        <div className="space-y-3">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-40 w-full" />
        </div>
      </CardContent>
    </Card>
  );
}

function LoadError({ message }: { message?: string }) {
  return (
    <Alert variant="destructive">
      <AlertTriangleIcon />
      <AlertDescription>
        {message ?? 'Could not load the stock clips.'}
      </AlertDescription>
    </Alert>
  );
}

/** One clinic's services and the stock clips each one uses, picked by name. */
function ClinicStockFootage() {
  const [organization, setOrganization] = useState<SelectedOrg | null>(null);

  return (
    <div className="space-y-4">
      <OrgCombobox
        className="w-full sm:w-[360px]"
        value={organization}
        onChange={setOrganization}
      />
      {organization ? (
        <OrganizationStockFootage
          key={organization.id}
          organizationId={organization.id}
        />
      ) : (
        <Card className="border-dashed py-12 text-center">
          <CardContent>
            <FilmIcon className="text-muted-foreground mx-auto mb-4 size-8" />
            <h2 className="font-semibold">Choose a clinic</h2>
            <p className="text-muted-foreground mt-1 text-sm">
              See what each of its services was read as and which clips its
              videos use, and change either.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
